import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { REG_HUB_DEFAULT_LANGS, REG_HUB_TRANSLOCO_CONFIG } from './reg-hub-transloco-config';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private transloco = inject(TranslocoService);
  private config = inject(REG_HUB_TRANSLOCO_CONFIG);

  readonly availableLangs = this.config.availableLangs ?? REG_HUB_DEFAULT_LANGS;

  /** Emits the active language, including the current value on subscribe. */
  readonly langChanges$: Observable<string> = this.transloco.langChanges$;

  get currentLang(): string {
    return this.transloco.getActiveLang();
  }

  isSupported(lang: string | null | undefined): lang is string {
    return !!lang && this.availableLangs.includes(lang);
  }

  /** Applies the saved language. Called once at startup by provideRegHubTransloco. */
  restoreSavedLanguage(): void {
    const saved = this.readSaved();
    if (this.isSupported(saved)) {
      this.transloco.setActiveLang(saved);
    }
  }

  /** Loads the translation for a language, then activates and persists it. */
  setLanguage(lang: string): void {
    if (!this.isSupported(lang)) {
      return;
    }
    this.transloco.load(lang).subscribe(() => {
      this.transloco.setActiveLang(lang);
      this.save(lang);
    });
  }

  toggleLanguage(): void {
    this.setLanguage(this.currentLang === 'en' ? 'fr' : 'en');
  }

  private readSaved(): string | null {
    try {
      return localStorage.getItem(this.config.storageKey);
    } catch {
      return null;
    }
  }

  private save(lang: string): void {
    try {
      localStorage.setItem(this.config.storageKey, lang);
    } catch {
      // storage unavailable (private mode etc.) - language still applies for this session
    }
  }
}
